<template>
  <div class="clinic-form-box">
    <div class="row justify-content-md-start">
      <div class="col-md-4">
        <ul class="anchor-menu">
          <li :class="{'active': activeAnchor === 'general'}">
            <a @click="setActiveAnchor('general')">{{ $t('createClinic.generalData') }}</a>
          </li>
          <li :class="{'active': activeAnchor === 'localization'}">
            <a @click="setActiveAnchor('localization')">{{ $t('createClinic.location') }}</a>
          </li>
          <li :class="{'active': activeAnchor === 'configuration'}">
            <a @click="setActiveAnchor('configuration')">{{ $t('createClinic.configuration') }}</a>
          </li>
          <li :class="{'active': activeAnchor === 'locale'}">
            <a @click="setActiveAnchor('locale')">{{ $t('createClinic.language') }}</a>
          </li>
          <li :class="{'active': activeAnchor === 'security'}">
            <a @click="setActiveAnchor('security')">{{ $t('createClinic.security') }}</a>
          </li>
        </ul>
      </div>

      <div class="col-md-8 offset-md-4 scrollbar" id="sectionsBox" ref="scrollContainer" v-if="clinic">
        <section id="general">
          <h3 class="sub-title" ref="targets">{{ $t('createClinic.generalData') }}</h3>
          <div class="form-floating">
            <vee-field name="name" v-model="clinic.name" rules="required" v-slot="{ field, errorMessage }">
              <input v-bind="field" type="text" class="form-control" ref="name" id="name" :placeholder="$t('createClinic.name')" :disabled="!isCurrentClinic" data-test="inputClinicName" autofocus>
              <span class="vee-error">{{ errorMessage }}</span>
            </vee-field>
            <label for="name">{{ $t('createClinic.name') }}*</label>
          </div>
          <div class="form-floating">
            <vee-field name="fantasyName" v-model="clinic.fantasyName" rules="required" v-slot="{ field, errorMessage }">
              <input v-bind="field" type="text" class="form-control" ref="fantasyName" id="fantasyName" :placeholder="$t('createClinic.nameExhibition')" :disabled="!isCurrentClinic" data-test="inputClinicFantasyName" >
              <span class="vee-error">{{ errorMessage }}</span>
            </vee-field>
            <label for="fantasyName">{{ $t('createClinic.nameExhibition') }}*</label>
          </div>
          <div class="form-floating">
            <vee-field name="document" v-model="clinic.document" v-slot="{ field, errorMessage }">
              <input v-bind="field" type="text" class="form-control" ref="document" id="document" v-maska
                     :data-maska="$t('inputMask.clinicDocument')" :placeholder="$t('createClinic.document')" :disabled="!isCurrentClinic" data-test="inputClinicDocument" >
              <span class="vee-error">{{ errorMessage }}</span>
            </vee-field>
            <label for="document">{{ $t('createClinic.document') }}</label>
          </div>
          <!-- end verify -->
          <div class="form-floating">
            <vee-field name="email" v-model="clinic.email" rules="email" v-slot="{ field, errorMessage }">
              <input v-bind="field" type="text" class="form-control" ref="email" id="email" :placeholder="$t('createClinic.email')" :disabled="!isCurrentClinic" data-test="inputClinicEmail" >
              <span class="vee-error">{{ errorMessage }}</span>
            </vee-field>
            <label for="email">{{ $t('createClinic.email') }}</label>
          </div>
          <div class="form-floating">
            <input type="text" class="form-control" id="telephone1" v-model="clinic.telephone1" :placeholder="$t('createClinic.telephone1')" :disabled="!isCurrentClinic" data-test="inputClinicPhone1" >
            <label for="telephone1">{{ $t('createClinic.telephone1') }}</label>
          </div>
          <div class="form-floating">
            <input type="text" class="form-control" id="telephone2" v-model="clinic.telephone2" :placeholder="$t('createClinic.telephone2')" :disabled="!isCurrentClinic" data-test="inputClinicPhone2" >
            <label for="telephone2">{{ $t('createClinic.telephone2') }}</label>
          </div>

          <div class="form-floating v-select-floating" v-if="is_creating">
            <v-select class="v-select form filter" v-model="parentClinicSelected" data-test="vSelectClinic" :placeholder="$t('createClinic.parentClinic')" :reduce="x => x.value" :options="parent_clinic_options" :disabled="!isCurrentClinic" @option:selected="emitParentClinic">
            </v-select>
            <label for="title">{{ $t('createClinic.parentClinic') }}</label>
          </div>
        </section>

        <section id="localization">
          <h3 class="sub-title" ref="targets">{{ $t('createClinic.location') }}</h3>
          <div class="form-floating">
            <vee-field name="address" v-model="clinic.address" rules="required" v-slot="{ field, errorMessage }">
              <input v-bind="field" type="text" class="form-control" ref="address" id="address" :disabled="!isCurrentClinic" :placeholder="$t('createClinic.address')" data-test="inputClinicAddress">
              <span class="vee-error">{{ errorMessage }}</span>
            </vee-field>
            <label for="address">{{ $t('createClinic.address') }}</label>
          </div>
          <div class="form-floating">
            <input type="text" class="form-control" id="number" v-model="clinic.addressNumber" :placeholder="$t('createClinic.number')" :disabled="!isCurrentClinic" data-test="inputClinicNumber" >
            <label for="number">{{ $t('createClinic.number') }}</label>
          </div>
          <div class="form-floating">
            <input type="text" class="form-control" id="district" v-model="clinic.district" :placeholder="$t('createClinic.district')" :disabled="!isCurrentClinic" data-test="inputClinicDistrict" >
            <label for="district">{{ $t('createClinic.district') }}</label>
          </div>
          <div class="form-floating">
            <vee-field name="city" v-model="clinic.city" rules="required" v-slot="{ field, errorMessage }">
              <input v-bind="field" type="text" class="form-control" ref="city" id="city" :placeholder="$t('createClinic.city')" :disabled="!isCurrentClinic" data-test="inputClinicCity">
              <span class="vee-error">{{ errorMessage }}</span>
            </vee-field>
            <label for="city">{{ $t('createClinic.city') }}</label>
          </div>
          <div class="form-floating">
            <vee-field name="state" v-model="clinic.state" rules="required" v-slot="{ field, errorMessage }">
              <input v-bind="field" type="text" class="form-control" ref="state" id="state" :placeholder="$t('createClinic.state')" :disabled="!isCurrentClinic" data-test="inputClinicState">
              <span class="vee-error">{{ errorMessage }}</span>
            </vee-field>
            <label for="state">{{ $t('createClinic.state') }}</label>
          </div>
          <div class="form-floating v-select-floating">
            <vee-field name="country" rules="required" v-slot="{ field, errorMessage }">
              <v-select
                v-bind="field"
                class="v-select form"
                v-model="countrySelected"
                label="label"
                :reduce="x => x.value"
                :options="countryOptions"
                :placeholder="$t('createClinic.country')"
                :disabled="!isCurrentClinic"
                @option:selected="emitOptions"
                data-test="vSelectCountry">
                <template #search="{attributes, events}">
                  <input
                    class="vs__search"
                    :required="!countrySelected"
                    v-bind="attributes"
                    v-on="events"
                  />
                </template>
              </v-select>
              <span class="vee-error">{{ errorMessage }}</span>
            </vee-field>
            <label for="countrySelected">{{ $t('createClinic.country') }}</label>
          </div>
        </section>

        <section id="configuration">
          <h3 class="sub-title" ref="targets">{{ $t('createClinic.configuration') }}</h3>
          <div class="form-floating v-select-floating">
            <v-select
              label="label"
              class="v-select form"
              id="mandatoryDocument"
              :placeholder="$t('createClinic.mandatoryDocument')"
              :disabled="!isCurrentClinic"
              v-model="mandatoryDocument"
              data-test="vSelectMandatoryDocument"
              :reduce="x => x.value"
              :options="configOptions"
              @option:selected="emitOptions">
            </v-select>
            <label for="mandatoryDocument">{{ $t('createClinic.mandatoryDocument') }}</label>
          </div>
        </section>

        <section id="locale">
          <h3 class="sub-title" ref="targets">{{ $t('createClinic.language') }}</h3>
          <div class="form-floating v-select-floating">
            <v-select
              class="v-select form"
              id="languageSelected"
              :placeholder="$t('createClinic.language')"
              :disabled="!isCurrentClinic"
              :reduce="x => x.value"
              v-model="languageSelected"
              data-test="vSelectLanguage"
              :options="languageOptions"
              @option:selected="emitOptions">
            </v-select>
            <label for="languageSelected">{{ $t('createClinic.language') }}</label>
          </div>
        </section>

        <section id="security">
          <h3 class="sub-title" ref="targets">{{ $t('createClinic.security') }}</h3>

          <div class="form-floating v-select-floating" :class="{ 'disabled': !isCurrentClinic }">
            <v-select
              label="label"
              class="v-select form"
              id="mfaMode"
              :placeholder="$t('createClinic.mfaMode')"
              v-model="mfaMode"
              data-test="vSelectMfa"
              :reduce="x => x.value"
              :options="mfaOptions"
              @option:selected="mfaModeChanged"
              :disabled="!isCurrentClinic">
            </v-select>
            <label for="mfaMode">{{ $t('createClinic.mfaMode') }}</label>
          </div>

          <div>
            <p>
              {{ $t('createClinic.messagePasswordExpired') }}
            </p>

            <div class="form-floating v-select-floating">
              <v-select
                label="label"
                class="v-select form"
                id="passwordExpirationDays"
                :placeholder="$t('createClinic.passwordExpirationDays')"
                :disabled="!isCurrentClinic"
                v-model="clinic.passwordExpirationDays"
                data-test="vSelectPasswordExpirationDays"
                :reduce="x => x.value"
                :options="passwordExpirationOptions"
              >
              </v-select>
              <label for="passwordExpirationDays">{{ $t('createClinic.passwordExpirationDays') }}</label>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
p {
  color:                         var(--Brand-Primary);
}

.has-alert {
  .col-md-8 {
    max-height:                  calc(100vh - 396px);
  }
}

@media (max-width: 991px) {
  .col-md-8 {
    max-height:                  calc(100dvh - 308px) !important;
    padding:                     0 15px 0 24px;
  }

  .has-alert {
    .col-md-8 {
      max-height:                calc(100dvh - 379px) !important;
    }
  }
}

@media (max-width: 1500px) {
  .col-md-8 {
    max-height:                  calc(100vh - 318px);
  }

  .has-alert {
    .col-md-8 {
      max-height:                calc(100vh - 390px);
    }
  }
}

@media screen and (min-width: 992px) and (max-width: 1365px) {
  .col-md-8 {
    max-height:                  calc(100vh - 303px);
  }
}

</style>

<script>
import { useUserStore } from '@/store/user';
export default {
  name: 'ClinicFormData',
  props: [
    'clinic',
    'disabled',
    'is_creating',
    'parent_clinic_options',
    'setFieldValue',
    'setFieldTouched',
    'validateForm',
    'resetVeeForm',
    'formMeta'
  ],
  data: () => ({
    countryOptions: [],
    configOptions: [],
    mfaOptions: [],
    languageOptions: [],
    passwordExpirationOptions: [],
    countrySelected: '',
    mandatoryDocument: 'none',
    languageSelected: '',
    mfaMode: null,
    security: {},
    activeAnchor: 'general',
    clinicTemplate: null,
    parentClinicSelected: null,
  }),

  watch: {
    disabled: function(newVal) {
      if (!newVal) {
        this.$nextTick(() => {
          this.$refs.name.focus()
        })
      }
    },

    'clinic.country': {
      immediate: true,
      handler(newCountry) {
        if (newCountry && this.countryOptions.length > 0) {
          this.$nextTick(() => {
            this.setVeeValidateFields();
          });
        }
      }
    },
  },

  computed: {
    isCurrentClinic() {
      if (this.is_creating){
        return true
      }

      const userStore = useUserStore()
      return this.clinic.id === userStore.sessionInfo.currentClinic.id
    }
  },

  mounted() {
    this.configOptions = [
      {value: 'none', label: this.$i18n.t('none')},
      {value: 'mrn', label: this.$i18n.t('mrn')},
      {value: 'document0', label: this.$i18n.t('document0')},
      {value: 'document1', label: this.$i18n.t('document1')},
      {value: 'document2', label: this.$i18n.t('document2')},
      {value: 'document3', label: this.$i18n.t('document3')},
    ];

    this.mfaOptions = [
      {value: 'disabled', label: this.$i18n.t('mfa.disabled')},
      {value: 'optional', label: this.$i18n.t('mfa.optional')},
      {value: 'mandatory', label: this.$i18n.t('mfa.mandatory')}
    ]

    this.languageOptions = [
      {value: 'pt-br', label: this.$i18n.t('locales.pt-br')},
      {value: 'en', label: this.$i18n.t('locales.en')},
      {value: 'es', label: this.$i18n.t('locales.es')},
    ];

    this.passwordExpirationOptions = [
      { label: this.$t('createClinic.neverExpires'), value: 0 },
      { label: '30 ' + this.$t('createClinic.days'), value: 30 },
      { label: '60 ' + this.$t('createClinic.days'), value: 60 },
      { label: '90 ' + this.$t('createClinic.days'), value: 90 },
      { label: '180 ' + this.$t('createClinic.days'), value: 180 },
      { label: '360 ' + this.$t('createClinic.days'), value: 360 },
    ],

    this.setLocaleConfig().then(() => {
      this.$nextTick(() => {
        this.setVeeValidateFields();
      });
    });

    const divElement = document.getElementById('sectionsBox')
    divElement.addEventListener('scroll', this.updateActiveAnchor);
    this.updateActiveAnchor(); // Initialize the active section
  },
  beforeUnmount() {
    const divElement = document.getElementById('sectionsBox')
    divElement.removeEventListener('scroll', this.updateActiveAnchor);
  },
  methods: {
    setActiveAnchor(anchor) {
      this.activeAnchor = anchor;
      this.$nextTick(() => {
        const headerHeight = 62;
        const element = document.getElementById(anchor);
        const container = this.$refs.scrollContainer;

        if (element && container) {
          const elementPosition = element.offsetTop;
          const offsetPosition = elementPosition - headerHeight;

          container.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      });
    },
    async setLocaleConfig() {
      let locale = this.clinic.locale
      this.countryOptions = []

      const countryCodeList = await this.$utils.countryCodeList(locale)

      if (locale) {
        for (let code of countryCodeList) {
          this.countryOptions.push({
            value: code,
            label: this.$i18n.t(`country.${code}`)
          })
        }
      }

      const initialCountryValue = this.clinic.country;
      const foundOption = this.countryOptions.find(opt => opt.value === initialCountryValue);
      this.countrySelected = foundOption ? initialCountryValue : null

      this.mandatoryDocument = this.clinic.mandatoryDocument
      this.languageSelected = this.clinic.locale

      let security = this.$clinicService.getSecurityConfiguration(this.clinic)
      this.mfaMode = security.mfaMode

      return Promise.resolve();
    },
    setVeeValidateFields() {
      if (this.clinic.country && this.setFieldValue && this.setFieldTouched) {
        this.setFieldValue('country', this.clinic.country)
        this.setFieldTouched('country', true)
      }
      if (this.clinic.name && this.setFieldValue && this.setFieldTouched) {
        this.setFieldValue('name', this.clinic.name)
        this.setFieldTouched('name', true)
      }
      if (this.clinic.fantasyName && this.setFieldValue && this.setFieldTouched) {
        this.setFieldValue('fantasyName', this.clinic.fantasyName)
        this.setFieldTouched('fantasyName', true)
      }
      if (this.clinic.address && this.setFieldValue && this.setFieldTouched) {
        this.setFieldValue('address', this.clinic.address)
        this.setFieldTouched('address', true)
      }
      if (this.clinic.city && this.setFieldValue && this.setFieldTouched) {
        this.setFieldValue('city', this.clinic.city)
        this.setFieldTouched('city', true)
      }
      if (this.clinic.state && this.setFieldValue && this.setFieldTouched) {
        this.setFieldValue('state', this.clinic.state)
        this.setFieldTouched('state', true)
      }
    },
    emitOptions() {
      let options = {
        country: this.countrySelected,
        mandatoryDocument: this.mandatoryDocument,
        locale: this.languageSelected,
      }

      this.$nextTick(() => {
        this.$emit('option-selected', options)
      })
    },
    emitParentClinic() {
      this.$emit('parent-changed', {
        parentClinic: this.parentClinicSelected
      })
    },
    mfaModeChanged() {
      this.$emit('security-changed', {
        mfaMode: this.mfaMode,
      })
    },
    updateActiveAnchor() {
      const sections = ['general', 'localization', 'configuration', 'locale', 'security'];
      const divElement = document.getElementById('sectionsBox')
      const divHeight = divElement.offsetHeight
      let currentActive;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (!element) continue;

        const rect = element.getBoundingClientRect();
        if (rect.top <= divHeight / 2 && rect.bottom >= divHeight / 2) {
          currentActive = section;
          break;
        }
      }

      if (currentActive && currentActive !== this.activeAnchor) {
        this.activeAnchor = currentActive;
      }
    }
  },
}
</script>
